import { useCallback, useEffect } from 'react';
import { connect, ConnectedProps, useDispatch } from 'react-redux';
import type { HfsOperation } from 'fleet-data-model';
import type {
  AssetPowerUpdatedPayload,
  AssetStatusUpdatedPayload,
  AssetLocationUpdatedPayload,
  AssetConnectivityUpdatedPayload,
  AssetHfsActionsUpdatedPayload,
  AssetFirmwareUpdatedPayload,
  AssetCSFirmwareUpdatedPayload,
} from '@fleet-websocket-service/api-contract';
import type { LocationFields } from '~/data/Asset';
import type { AppState } from '~/reducers';
import type { AppDispatch } from '~/store/Store';
import type { Callback } from '~/utils/PubSub/PubSub';
import SocketEventType from '~/constants/socket-event-type';
import Logger from '~/utils/logger/logger';
import { HfsOperationStateReflectionPayload } from '~/domain/assets/models/HfsOperationStateReflectionPayload';
import { reflectOperationStatusUpdate as reflectSecurityOperationStatusUpdateAction } from '~/domain/roboticmower-details/store/securitySettings/reducer';
import { reflectOperationStatusUpdate as reflectInstallationOperationStatusUpdateAction } from '~/domain/roboticmower-details/components/RoboticInstallationCard/reducer';
import { socketReceiveRoboticMower as socketReceiveRoboticMowerAction } from '~/ducks/robotic-mower-command/actions';
import {
  reflectOperationStatusChange as reflectOperationStatusChangeAction,
  reflectAssetHfsActionsUpdate as reflectAssetHfsActionsUpdateAction,
} from '~/domain/assets/store/hfs-operations/reducer';
import { registerHfsActionsUpdateEvent as registerHfsActionsUpdateEventAction } from '~/domain/assets/store/hfs-actions/reducer';
import {
  refreshAssetCreatedDate as refreshAssetCreatedDateAction,
  refreshAssetDeletedDate as refreshAssetDeletedDateAction,
} from '~/domain/assets/store/assets-counts/reducer';
import {
  partialUpdateAsset as partialUpdateAssetAction,
  updateAssetLocation as updateAssetLocationAction,
  updateAssetConnectivity as updateAssetConnectivityAction,
  updateAssetPower as updateAssetPowerAction,
  updateAssetFirmware as updateAssetFirmwareAction,
  updateAssetCSFirmware as updateAssetCSFirmwareAction,
  updateAssetStatus as updateAssetStatusAction,
  updateAssetHfsActions as updateAssetHfsActionsAction,
} from '~/ducks/asset-updates/actions';
import { reflectOperationStatusUpdate as reflectMowerCentricOperationStatusUpdateAction } from '~/domain/roboticmower-details/store/mowerCentricSettings/reducer';
import { reflectFixedLocationOperationStatusUpdate as reflectFixedLocationOperationStatusUpdateAction } from '~/domain/asset-details-common/store/fixedLocation/reflect-fixed-location-operation';
import { setOutdated as setOutdatedAction } from '~/domain/asset-details-common/store/mapDetails/actions';
import { updateWsReconnectionTime as updateWsReconnectionTimeAction } from '~/components/containers/AppContainer/actions';
import useWsService from '~/hooks/use-ws-service';
import { getIsHfsActionsUpdateRelevant } from '~/domain/assets/utils/actions-selectors';
import useUpdateAssetMapQuery from '~/domain/maps/hooks/use-update-asset-map-query';
import { mapRoboticMowerSocketMessage } from './utils';
import type { RoboticMowerSocketMessage } from './types';

export type Props = ReduxProps;
export const WebSocketReceiver = ({
  assetHfsActionsUpdateEventsTimestamps,
  partialUpdateAsset,
  setOutdated,
  updateWsReconnectionTime,
  socketReceiveRoboticMower,
  reflectSecurityOperationStatusUpdate,
  reflectInstallationOperationStatusUpdate,
  reflectMowerCentricOperationStatusUpdate,
  reflectFixedLocationOperationStatusUpdate,
  reflectOperationStatusChange,
  updateAssetLocation,
  updateAssetConnectivity,
  updateAssetPower,
  updateAssetFirmware,
  reflectAssetHfsActionsUpdate,
  updateAssetCSFirmware,
  updateAssetStatus,
  updateAssetHfsActions,
  refreshAssetCreatedDate,
  refreshAssetDeletedDate,
  registerHfsActionsUpdateEvent,
}: Props) => {
  const dispatch = useDispatch<AppDispatch>();
  const socketService = useWsService();
  const validateAssetHfsActionsUpdateEvent = useCallback(
    (iprId: string, timestamp: string) => {
      const previousTimestamp = assetHfsActionsUpdateEventsTimestamps.find(
        (item) => item.iprId === iprId
      )?.timestamp;
      return getIsHfsActionsUpdateRelevant(timestamp, previousTimestamp);
    },
    [assetHfsActionsUpdateEventsTimestamps]
  );
  const { updateAssetMapLocationQuery, updateAssetMapStatusQuery } = useUpdateAssetMapQuery();
  const onSocketReceiveMower = useCallback(
    (data: RoboticMowerSocketMessage) => {
      Logger.log('onSocketReceiveMower', data);
      const assetId = data.object.id;
      const mappedRoboticMowerData = mapRoboticMowerSocketMessage(data);

      partialUpdateAsset({
        assetId,
        asset: mappedRoboticMowerData,
        forceUpdate: false,
      });

      socketReceiveRoboticMower({
        reason: data.reason,
        roboticMower: data.object,
      });
    },
    [partialUpdateAsset, socketReceiveRoboticMower]
  );

  const onSocketReceiveAssetLocationUpdated = useCallback(
    (data: { assetId: string; payload: AssetLocationUpdatedPayload }) => {
      Logger.log('onSocketReceiveAssetLocationUpdated', data);
      const coordinates = {
        latitude: data.payload.lat,
        longitude: data.payload.lng,
      };
      // @ts-expect-error payload carries only the reported fields
      const location: LocationFields = {
        placeId: data.payload.placeId,
        timezoneId: data.payload.timezoneId,
        reportedByDevice: data.payload.reportedByDevice,
        ...coordinates,
      };
      updateAssetLocation({
        assetId: data.assetId,
        location,
      });

      updateAssetMapLocationQuery(data.assetId, coordinates.latitude, coordinates.longitude);
    },
    [updateAssetLocation, updateAssetMapLocationQuery]
  );

  const onSocketReceiveAssetConnectivityUpdated = useCallback(
    (data: { assetId: string; payload: AssetConnectivityUpdatedPayload }) => {
      Logger.log('onSocketReceiveAssetConnectivityUpdated', data);
      updateAssetConnectivity({
        assetId: data.assetId,
        connectivity: data.payload,
      });
    },
    [updateAssetConnectivity]
  );

  const onSocketReceiveAssetDeleted = useCallback(
    (data: { assetId: string }) => {
      Logger.log('onSocketReceiveAssetDeleted', data);
      dispatch(refreshAssetDeletedDate());
    },
    [dispatch, refreshAssetDeletedDate]
  );

  const onSocketReceiveAssetCreated = useCallback(
    (data: { assetId: string }) => {
      Logger.log('onSocketReceiveAssetCreated', data);
      dispatch(refreshAssetCreatedDate());
    },
    [dispatch, refreshAssetCreatedDate]
  );

  const onSocketReceiveAssetPowerUpdated = useCallback(
    (data: { assetId: string; payload: AssetPowerUpdatedPayload }) => {
      Logger.log('onSocketReceiveAssetPowerUpdated', data);
      updateAssetPower({
        assetId: data.assetId,
        power: data.payload,
      });
    },
    [updateAssetPower]
  );

  const onSocketReceiveAssetFirmwareUpdated = useCallback(
    (data: { assetId: string; payload: AssetFirmwareUpdatedPayload }) => {
      Logger.log('onSocketReceiveAssetFirmwareUpdated', data);
      updateAssetFirmware({
        assetId: data.assetId,
        version: data.payload.version,
      });
    },
    [updateAssetFirmware]
  );

  const onSocketReceiveAssetCSFirmwareUpdated = useCallback(
    (data: { assetId: string; payload: AssetCSFirmwareUpdatedPayload }) => {
      Logger.log('onSocketReceiveAssetCSFirmwareUpdated', data);
      updateAssetCSFirmware({
        assetId: data.assetId,
        version: data.payload.version,
      });
    },
    [updateAssetCSFirmware]
  );

  const onSocketReceiveAssetStatusUpdated = useCallback(
    (data: { assetId: string; payload: AssetStatusUpdatedPayload }) => {
      Logger.log('onSocketReceiveAssetStatusUpdated', data);
      updateAssetStatus({
        assetId: data.assetId,
        // @ts-expect-error infoItems are StatusInfoItems, not InfoItems
        status: data.payload,
      });

      updateAssetMapStatusQuery(data.assetId, data.payload.operationalStatus?.status);
    },
    [updateAssetStatus, updateAssetMapStatusQuery]
  );

  const onSocketReceiveAssetHfsActionsUpdated = useCallback(
    (data: { assetId: string; payload: AssetHfsActionsUpdatedPayload; timestamp: string }) => {
      if (validateAssetHfsActionsUpdateEvent(data.assetId, data.timestamp)) {
        Logger.log('onSocketReceiveAssetHfsActionsUpdated - handled', data);
        registerHfsActionsUpdateEvent({
          timestamp: data.timestamp,
          iprId: data.assetId,
        });
        updateAssetHfsActions({
          assetId: data.assetId,
          updated: data.payload.updated,
          previous: data.payload.previous,
        });
        reflectAssetHfsActionsUpdate({
          assetId: data.assetId,
          updated: data.payload.updated,
          previous: data.payload.previous,
        });
      } else {
        Logger.log('onSocketReceiveAssetHfsActionsUpdated - skipped', data);
      }
    },
    [
      validateAssetHfsActionsUpdateEvent,
      registerHfsActionsUpdateEvent,
      updateAssetHfsActions,
      reflectAssetHfsActionsUpdate,
    ]
  );

  const onSocketReceiveAssetHfsOperationsUpdated = useCallback(
    (data: { assetId: string; payload: HfsOperation }) => {
      Logger.log('onSocketReceiveAssetHfsOperationsUpdated', data);
      reflectOperationStatusChange(data.payload);
      reflectSecurityOperationStatusUpdate(data.payload as HfsOperationStateReflectionPayload);
      reflectInstallationOperationStatusUpdate(data.payload as HfsOperationStateReflectionPayload);
      reflectMowerCentricOperationStatusUpdate(data.payload as HfsOperationStateReflectionPayload);
      reflectFixedLocationOperationStatusUpdate(data.payload as HfsOperationStateReflectionPayload);
    },
    [
      reflectMowerCentricOperationStatusUpdate,
      reflectInstallationOperationStatusUpdate,
      reflectOperationStatusChange,
      reflectSecurityOperationStatusUpdate,
      reflectFixedLocationOperationStatusUpdate,
    ]
  );

  useEffect(() => {
    const events: [SocketEventType, Callback][] = [
      [SocketEventType.AssetConnectivityUpdated, onSocketReceiveAssetConnectivityUpdated],
      [SocketEventType.AssetCreated, onSocketReceiveAssetCreated],
      [SocketEventType.AssetDeleted, onSocketReceiveAssetDeleted],
      [SocketEventType.AssetLocationUpdated, onSocketReceiveAssetLocationUpdated],
      [SocketEventType.AssetPowerUpdated, onSocketReceiveAssetPowerUpdated],
      [SocketEventType.AssetFirmwareUpdated, onSocketReceiveAssetFirmwareUpdated],
      [SocketEventType.AssetCSFirmwareUpdated, onSocketReceiveAssetCSFirmwareUpdated],
      [SocketEventType.AssetStatusUpdated, onSocketReceiveAssetStatusUpdated],
      [SocketEventType.AssetHfsActionsUpdated, onSocketReceiveAssetHfsActionsUpdated],
      [SocketEventType.AssetHfsOperationUpdated, onSocketReceiveAssetHfsOperationsUpdated],
    ];

    const unsubscriptions = events.map(([type, cb]) => socketService.on(type, cb, false));

    return () => {
      unsubscriptions.forEach((unsubscribe) => unsubscribe());
    };
  }, [
    socketService,
    onSocketReceiveAssetConnectivityUpdated,
    onSocketReceiveAssetCreated,
    onSocketReceiveAssetDeleted,
    onSocketReceiveAssetLocationUpdated,
    onSocketReceiveAssetPowerUpdated,
    onSocketReceiveAssetFirmwareUpdated,
    onSocketReceiveAssetCSFirmwareUpdated,
    onSocketReceiveAssetStatusUpdated,
    onSocketReceiveAssetHfsActionsUpdated,
    onSocketReceiveAssetHfsOperationsUpdated,
  ]);

  useEffect(() => {
    return socketService.on(SocketEventType.ROBOTIC_MOWER, onSocketReceiveMower, false);
  }, [onSocketReceiveMower, socketService]);

  const onSocketReconnection = useCallback(() => {
    Logger.log('onSocketReconnection');
    setOutdated(true);
    updateWsReconnectionTime();
  }, [setOutdated, updateWsReconnectionTime]);

  useEffect(() => {
    return socketService.on(SocketEventType.Reconnection, onSocketReconnection, false);
  }, [onSocketReconnection, socketService]);

  return null;
};

const mapDispatchToProps = {
  socketReceiveRoboticMower: socketReceiveRoboticMowerAction,
  partialUpdateAsset: partialUpdateAssetAction,
  updateAssetLocation: updateAssetLocationAction,
  updateAssetConnectivity: updateAssetConnectivityAction,
  reflectOperationStatusChange: reflectOperationStatusChangeAction,
  reflectMowerCentricOperationStatusUpdate: reflectMowerCentricOperationStatusUpdateAction,
  reflectFixedLocationOperationStatusUpdate: reflectFixedLocationOperationStatusUpdateAction,
  updateAssetPower: updateAssetPowerAction,
  updateAssetFirmware: updateAssetFirmwareAction,
  updateAssetCSFirmware: updateAssetCSFirmwareAction,
  registerHfsActionsUpdateEvent: registerHfsActionsUpdateEventAction,
  updateAssetStatus: updateAssetStatusAction,
  updateAssetHfsActions: updateAssetHfsActionsAction,
  setOutdated: setOutdatedAction,
  updateWsReconnectionTime: updateWsReconnectionTimeAction,
  refreshAssetCreatedDate: refreshAssetCreatedDateAction,
  refreshAssetDeletedDate: refreshAssetDeletedDateAction,
  reflectAssetHfsActionsUpdate: reflectAssetHfsActionsUpdateAction,
  reflectSecurityOperationStatusUpdate: reflectSecurityOperationStatusUpdateAction,
  reflectInstallationOperationStatusUpdate: reflectInstallationOperationStatusUpdateAction,
};

const mapStateToProps = (state: AppState) => ({
  assetHfsActionsUpdateEventsTimestamps: state.assetHfsActions.updateEventsTimestamps,
});

const connector = connect(mapStateToProps, mapDispatchToProps);
type ReduxProps = ConnectedProps<typeof connector>;
export default connector(WebSocketReceiver);
